/**
 * useWillingnessRollPastEod — how late this shop is willing to run.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY THIS EXISTS
 * ═══════════════════════════════════════════════════════════════════════════
 * FLOYD-2. `TicketCard` hard-coded `willingnessRollPastEodMin: 0`, so the
 * card's EOD gate refused work the shop had explicitly said it would stay
 * late for. `useAutoBump`, `useEODMonitor` and `useOptiFinishPushback` all
 * read the real value from `shop_profile.shop_culture` — the card was the
 * only surface asserting a shop culture nobody configured.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * WHY IT IS A QUERY AND NOT A FETCH
 * ═══════════════════════════════════════════════════════════════════════════
 * A `TicketCard` renders once per ticket, and a busy Dayboard has dozens.
 * The sibling hooks each open their own `useEffect` fetch, which is fine for
 * one monitor and unacceptable per card. React Query gives every card on the
 * board ONE request and one cached answer.
 *
 * The value changes about as often as a shop rewrites its own culture, so it
 * is held for an hour rather than re-fetched on every mount and focus.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * Minutes past a technician's close that this shop tolerates.
 *
 * Returns 0 while loading and on error. Zero is the SAFE default here: it
 * makes the gate stricter, never laxer, so a failed read can flag work for
 * review but can never quietly promise a customer a car that will not be
 * ready.
 */
export function useWillingnessRollPastEod(shopId: string | null | undefined): number {
  const { data } = useQuery({
    queryKey: ['shop-willingness-roll-past-eod', shopId],
    enabled: !!shopId,
    staleTime: 60 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    queryFn: async (): Promise<number> => {
      const { data: profile, error } = await supabase
        .from('shop_profile')
        .select('shop_culture')
        .eq('shop_id', shopId as string)
        .maybeSingle();

      if (error) {
        console.error('[useWillingnessRollPastEod] read failed', error);
        return 0;
      }

      const culture = (profile?.shop_culture ?? {}) as Record<string, unknown>;
      const raw = Number(culture.willingness_roll_past_eod ?? 0);
      return Number.isFinite(raw) && raw > 0 ? raw : 0;
    },
  });

  return data ?? 0;
}
